"use client";

import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { getCookie, setCookie } from "@repo/client-cookies";
import {
  TRENDING_CATEGORY_ID_TOPSHOP,
  TRENDING_CATEGORY_ID_TOPMAN,
} from "@/constants";
import {
  getFloorCode,
  FloorKey,
  FloorCodeKey,
  determineFloor,
  BRAND_TO_FLOOR_MAP,
} from "./utils";

export type FloorContextType = {
  floor: FloorKey | null;
  code: FloorCodeKey | null;
  setFloor: (floor: FloorKey) => void;
  isTopshop: boolean;
  isTopman: boolean;
  trendingCategoryId: number | null;
  getTrendingUrl: (basePath?: string) => string;
};

const FLOOR_COOKIE_NAME = "floor";
const FloorContext = createContext<FloorContextType | null>(null);

export const FloorProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const [floor, setFloorState] = useState<FloorKey | null>(null);

  useEffect(() => {
    const determinedFloor = determineFloor(
      getCookie(FLOOR_COOKIE_NAME) || "",
      pathname || "",
    );

    if (determinedFloor) {
      setFloorState(determinedFloor);
      return;
    }
  }, [pathname]);

  const setFloor = useCallback((newFloor: FloorKey) => {
    const floorCode = getFloorCode(newFloor);

    setCookie(FLOOR_COOKIE_NAME, floorCode, {
      path: "/",
      expiresInMinutes: 60 * 60 * 24 * 365,
    });

    setFloorState(newFloor);
  }, []);

  const floorCode =
    floor && Object.keys(BRAND_TO_FLOOR_MAP).includes(floor)
      ? getFloorCode(floor as FloorKey)
      : null;

  const isTopshop = useMemo(() => floor === "topshop", [floor]);
  const isTopman = useMemo(() => floor === "topman", [floor]);

  const trendingCategoryId = useMemo(() => {
    if (!floor) return TRENDING_CATEGORY_ID_TOPSHOP;

    return floor === "topshop"
      ? TRENDING_CATEGORY_ID_TOPSHOP
      : TRENDING_CATEGORY_ID_TOPMAN;
  }, [floor]);

  const getTrendingUrl = useCallback(
    (basePath = "") => {
      if (!floor || !trendingCategoryId) return "#";
      return `${basePath}/${floor}/category/trending?cid=${trendingCategoryId}`;
    },
    [floor, trendingCategoryId],
  );

  const contextValue: FloorContextType = {
    floor,
    code: floorCode as FloorCodeKey | null,
    setFloor,
    isTopshop,
    isTopman,
    trendingCategoryId,
    getTrendingUrl,
  };

  return (
    <FloorContext.Provider value={contextValue}>
      {children}
    </FloorContext.Provider>
  );
};

export const useFloorContext = () => {
  const context = useContext(FloorContext);

  if (!context) {
    throw new Error("useFloorContext must be used within a FloorProvider");
  }

  return context;
};
